import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ToastService } from '../../shared/components/toast/toast.service';
import { extractErrorMessage } from '../../shared/core/services/http-error.util';
import { LoggerService } from '../../shared/core/services/logger.service';
import { AuthService } from '../services/auth.service';
import { SUPPRESS_ERROR_TOAST } from './http-context';

/** URLs qui gerent leurs propres erreurs (pas de toast global) */
const SILENT_URL_PATTERNS = ['/auth/token', '/auth/logout'];

/**
 * Le serveur refuse faute d'authentification, et non faute de droits.
 *
 * DRF répond 403 avec ce libellé quand la session n'est plus valide : « Accès refusé — informations
 * d'authentification non fournies » parlait alors de permissions à qui n'était simplement plus
 * connecté. Les deux formulations de `NotAuthenticated` sont couvertes, la traduction ayant varié
 * d'une version à l'autre.
 */
export function estSessionExpiree(message: string | null): boolean {
    return message !== null && /authentification\s+(?:non\s+fournies|n(?:'|’)ont\s+pas\s+[ée]t[ée]\s+fournies)/i.test(message);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const toast = inject(ToastService);
    const logger = inject(LoggerService);
    const authService = inject(AuthService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            // Ne pas afficher de toast pour les endpoints d'auth (geres par authInterceptor)
            // ou lorsque l'appelant a explicitement demande la suppression du toast via context
            const isSilent = SILENT_URL_PATTERNS.some((p) => req.url.includes(p)) || req.context.get(SUPPRESS_ERROR_TOAST);

            // Extraire le message d'erreur du body si disponible
            const serverMessage = extractErrorMessage(error);

            if (!isSilent) {
                signalerErreur(toast, error.status, serverMessage);
            }
            // La session est close : sans redirection, l'agent restait sur la page et recevait
            // le toast « Session expirée » a chacune de ses actions (audit R-23).
            // Seulement si un utilisateur etait connecte : un visiteur anonyme n'a pas de session a fermer.
            if (error.status === 403 && estSessionExpiree(serverMessage) && authService.user()) {
                authService.logout();
            }

            // En production, ne pas exposer l'URL interne ni le message serveur dans
            // la console navigateur (fuite de chemins d'API / details backend).
            if (environment.production) {
                logger.error(`[HTTP ${error.status}] ${req.method}`);
            } else {
                logger.error(`[HTTP ${error.status}] ${req.method} ${req.url}`, serverMessage || error.message);
            }

            return throwError(() => error);
        }),
    );
};

/** Toast correspondant au statut HTTP. Extrait de l'intercepteur pour en borner la complexite. */
function signalerErreur(toast: ToastService, status: number, serverMessage: string | null): void {
    switch (true) {
        case status === 0:
            toast.error('Erreur réseau', 'Impossible de contacter le serveur.');
            break;
        case status === 400:
            toast.error('Erreur de validation', serverMessage || 'Données invalides.');
            break;
        case status === 401:
            // Gere par authInterceptor (refresh token)
            break;
        // Deux refus distincts sous le même statut : la session perdue, qui se
        // règle par une reconnexion, et le droit manquant, sur lequel l'agent ne peut rien.
        case status === 403 && estSessionExpiree(serverMessage):
            toast.error('Session expirée', 'Veuillez vous reconnecter.');
            break;
        case status === 403:
            toast.error('Accès refusé', serverMessage || "Vous n'avez pas les permissions nécessaires.");
            break;
        case status === 404:
            toast.error('Introuvable', "La ressource demandée n'existe pas.");
            break;
        // 405 : methode non routee cote serveur (ex. DELETE /files/{id}/, non expose).
        // Volontairement SANS toast : c'est un defaut de contrat d'API, sur lequel
        // l'utilisateur ne peut rien et qui ne decrit rien de son action. La trace
        // reste entiere dans la console via le logger de l'intercepteur.
        case status === 409:
            toast.error('Conflit', serverMessage || 'Cette ressource existe déjà.');
            break;
        case status === 413:
            toast.error('Fichier trop volumineux', 'Le serveur refuse un envoi de cette taille.');
            break;
        case status === 422:
            toast.error('Données invalides', serverMessage || "Le serveur n'a pas pu traiter la demande.");
            break;
        case status === 429:
            toast.error('Trop de requêtes', 'Veuillez patienter avant de réessayer.');
            break;
        case status >= 500:
            toast.error('Erreur serveur', 'Une erreur interne est survenue.');
            break;
    }
}
